/**
 * Storage service — typed wrapper over Supabase Storage.
 *
 * Centralises uploads/removals for the three project buckets and enforces the
 * `<family_id>/<entity_id>/<filename>` path convention that the storage RLS
 * policies match on (see migration 016_provision_storage_buckets.sql, which
 * re-provisions what 005 declared but never actually created).
 *
 * The Supabase client is created lazily by `getSupabaseClient()`, so importing
 * this module performs no network work. These functions throw if invoked
 * without Supabase configured so a mis-wire fails loudly rather than silently
 * hitting the network.
 *
 * PUBLIC VS PRIVATE, AND WHY THE TYPES CARE
 *   Migration 020 flips `photos` to a PRIVATE bucket. Before it, an
 *   unauthenticated GET with no apikey and no Authorization header returned a
 *   family photo whose row was marked `visibility = 'private'` — verified live.
 *   Tightening the storage.objects SELECT policy alone was proven to be a
 *   placebo: while a bucket is public the /object/public/ route never consults
 *   that policy at all (the already-private `attachments` bucket answers
 *   "Bucket not found" on that same route, which is what a policy-consulting
 *   route does NOT do).
 *
 *   `avatars` stays public — that was the explicit product decision. So exactly
 *   one bucket has a meaningful public URL, and {@link getPublicUrl} and the
 *   `publicUrl` field of an upload result are typed to that bucket only. This
 *   is not decoration. `uploadFile` used to hand back `{ path, publicUrl }` for
 *   EVERY bucket, including the private `attachments` bucket, where the
 *   returned URL has always 404'd — a caller that reached for `.publicUrl` got
 *   a string that looks like a working link and is not one. PhotoUploader did
 *   exactly that and wrote the result into `photos.url`. The overloads below
 *   make that mistake unrepresentable: ask a private bucket for an upload
 *   result and there is no `publicUrl` property to reach for.
 *
 * READS ON A PRIVATE BUCKET
 *   Every render path signs at read time. What is STORED is the raw object key
 *   (`<family_id>/<album_id>/<file>`) — never a signed URL, because a signed
 *   URL expires while `photos.url` is `not null` and permanent, the offline
 *   mutation queue persists insert payloads into IndexedDB and would replay a
 *   pre-expired one, and a `?token=` in the column empties
 *   cleanup-orphan-storage's exact-match index (its `extractObjectKey` returns
 *   null for anything containing '?', so every photo would fall through to the
 *   loose fallback).
 *
 *   `photos.url` IS THE ONLY STORED KEY. `photos.thumbnail_url` carried a
 *   second copy of that same key on every row this app has ever written, and
 *   since 020 made the bucket private there is nothing renderable to store
 *   twice: a thumbnail is a READ-TIME TRANSFORM PARAMETER of the signing call,
 *   not a column. Migration 024 makes that column nullable with a default — so
 *   the old bundle, which still sends it, and the new one, which does not, both
 *   work against the intermediate schema — and 025 physically drops it once the
 *   new bundle is being served. Nothing in this module ever read it.
 *
 *   {@link createSignedUrls} is the batch signer and is the one to reach for on
 *   any list path; it CANNOT transform. {@link createSignedUrl} signs one
 *   object and is for the single-object paths (an album cover, a download link)
 *   or when a `transform` is wanted — which is the whole of this project's
 *   resizing: `ALBUM_COVER_TRANSFORM` in supabaseAlbumService is the one caller
 *   that uses it, and the album grid takes full-size bytes at tile size on
 *   purpose.
 *
 * @module shared/services/storageService
 */

import { getSupabaseClient } from '@/shared/clients/supabaseClient';
import { getCurrentFamilyId } from '@/shared/lib/familyContext';
import { sanitizeStorageObjectName } from '@/shared/lib/sanitize';
import { monitoringService } from '@/shared/services/monitoringService';

/**
 * Buckets whose objects are readable by anyone holding the URL, forever.
 *
 * `avatars` only, deliberately: a family avatar is low-sensitivity and is
 * rendered in a hundred places where an expiring URL would be a liability.
 * Nothing else may join this list without the same explicit decision.
 */
export type PublicStorageBucket = 'avatars';

/** Buckets that are readable only through a signed URL. */
export type PrivateStorageBucket = 'photos' | 'attachments';

/** Storage buckets declared in supabase/config.toml + migrations 005/016/020. */
export type StorageBucket = PublicStorageBucket | PrivateStorageBucket;

/** Single source of truth for the split above. */
const PUBLIC_BUCKETS: ReadonlySet<StorageBucket> = new Set<StorageBucket>([
  'avatars',
]);

/** Narrow to the public set so a public URL is only built where it means something. */
function isPublicBucket(bucket: StorageBucket): bucket is PublicStorageBucket {
  return PUBLIC_BUCKETS.has(bucket);
}

interface BucketConfig {
  /** Max file size in bytes. */
  readonly maxBytes: number;
  /** Allowed MIME types, or `null` for any type. */
  readonly allowedMimeTypes: readonly string[] | null;
  /**
   * Allowed lowercase extensions, dot included, or `null` to not check the
   * extension at all.
   *
   * Only `attachments` sets this, and it changes how a BLANK `file.type` is
   * treated — see {@link validateFile}.
   */
  readonly allowedExtensions: readonly string[] | null;
  /** Bulgarian message shown when a file's type or extension is refused. */
  readonly typeErrorMessage: string;
}

const IMAGE_MIME_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
];

/**
 * What the private `attachments` bucket accepts, in both of the forms a browser
 * can hand us.
 *
 * WHY THIS LIST EXISTS AT ALL. `attachments` used to carry
 * `allowedMimeTypes: null` — "any type" — and the only thing standing between
 * the bucket and an arbitrary executable was `DEFAULT_ACCEPTED_TYPES` in
 * FileUploadArea, fed to the file input's `accept` attribute. `accept` is a
 * PICKER HINT: it filters the dialog's default view, a user can switch it to
 * "All files", and drag-and-drop never consults it at all — `handleDrop` reads
 * `e.dataTransfer.files` and goes straight to upload. So the advertised list
 * and the enforced list were two different things, and only one of them was
 * enforced. These two constants mirror `DEFAULT_ACCEPTED_TYPES` exactly.
 *
 * BOTH lists are needed, not one. A browser reports `file.type` from its own
 * extension table and reports `''` when it has no entry — routinely for `.csv`
 * and for Office documents on a machine without Office installed — so a MIME
 * check alone rejects legitimate files. And an extension check alone is
 * trivially defeated by renaming. The rule in {@link validateFile} is
 * therefore: the extension must be on this list, and the reported type must be
 * on the other list WHENEVER the browser reported one.
 *
 * `ATTACHMENT_EXTENSIONS` is exported so FileUploadArea can build its `accept`
 * attribute from it instead of keeping a second hand-maintained copy. The hint
 * and the rule are now the same list by construction.
 */
const ATTACHMENT_MIME_TYPES: readonly string[] = [
  ...IMAGE_MIME_TYPES,
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

export const ATTACHMENT_EXTENSIONS: readonly string[] = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.txt',
  '.csv',
];

const IMAGE_TYPE_ERROR = 'Неподдържан тип файл. Разрешени са само изображения.';

const BUCKET_CONFIG: Readonly<Record<StorageBucket, BucketConfig>> = {
  avatars: {
    maxBytes: 5 * 1024 * 1024,
    allowedMimeTypes: IMAGE_MIME_TYPES,
    allowedExtensions: null,
    typeErrorMessage: IMAGE_TYPE_ERROR,
  },
  photos: {
    maxBytes: 50 * 1024 * 1024,
    allowedMimeTypes: IMAGE_MIME_TYPES,
    allowedExtensions: null,
    typeErrorMessage: IMAGE_TYPE_ERROR,
  },
  attachments: {
    maxBytes: 25 * 1024 * 1024,
    allowedMimeTypes: ATTACHMENT_MIME_TYPES,
    allowedExtensions: ATTACHMENT_EXTENSIONS,
    typeErrorMessage:
      'Неподдържан тип файл. Разрешени са изображения, PDF, Word, Excel, TXT и CSV.',
  },
};

/**
 * How long a signed URL stays valid, in seconds. Four hours.
 *
 * The number is a trade between two failure modes, and neither end is
 * hypothetical:
 *
 *   TOO SHORT — the URL dies while the page holding it is still open. An album
 *   tab left open over lunch, a photo viewer sitting on one image, a phone that
 *   slept mid-scroll: every one of those re-renders from a signature minted at
 *   load time, and nothing in this app re-signs on a timer. At 1 hour (the
 *   previous default, which is what the calendar's attachments still ran on)
 *   that is an ordinary afternoon, and the symptom is a broken-image icon with
 *   no explanation. The onError placeholders added in the albums tree stop it
 *   from looking broken; they do not bring the photo back.
 *
 *   TOO LONG — a URL that leaks is a bearer token for that object for its whole
 *   lifetime. Signed URLs end up in browser history, in the "copy image
 *   address" a family member pastes into a chat, in a Referer header, in a
 *   screenshot of the address bar. There is no revocation: the signature is a
 *   JWT the Storage API validates statelessly, so nothing short of moving the
 *   object invalidates it early. A day-long TTL means a link pasted at 9am is
 *   still live at bedtime.
 *
 * Four hours covers any realistic single sitting and keeps a leaked link from
 * outliving the session that produced it. It applies to `attachments` too,
 * which is a straight improvement — calendar attachment links used to expire in
 * an hour for no considered reason.
 *
 * If this ever needs to be longer, the right fix is re-signing on demand (an
 * onError that re-signs once and retries), not a bigger number here.
 */
export const SIGNED_URL_TTL_SECONDS = 14400;

/**
 * Read-time image transformation, applied by the Storage render endpoint.
 *
 * Mirrors the subset of storage-js's `TransformOptions` this project uses.
 * Declared locally on purpose: `@supabase/storage-js` is a transitive
 * dependency, not a declared one, and `@supabase/supabase-js` does not
 * re-export the type — importing it directly would add a phantom dependency
 * that `npm run deps:check` cannot see and a lockfile bump could remove.
 * Structural typing makes this assignable to the real option bag.
 */
export interface ImageTransform {
  readonly width?: number;
  readonly height?: number;
  readonly resize?: 'cover' | 'contain' | 'fill';
  readonly quality?: number;
}

/** Options for {@link createSignedUrl}. */
export interface SignedUrlOptions {
  /**
   * Serve the object as a download. A string becomes the filename in the
   * `Content-Disposition` header the Storage API sets.
   *
   * This is the ONLY way to force a download from this app: an `<a download>`
   * attribute is ignored by every browser when the href is cross-origin, and
   * the Storage API is a different origin from the app. The download button in
   * PhotoViewer looked like it worked and merely opened the image in a tab.
   */
  readonly download?: string | boolean;
  /**
   * Resize / re-encode at read time. On a private bucket this replaces stored
   * thumbnails entirely: a resize is a parameter of the read, not a second
   * object someone has to create, keep in sync and eventually garbage-collect.
   *
   * NOT available on {@link createSignedUrls} — see the note there.
   */
  readonly transform?: ImageTransform;
}

/** Result of a successful upload into any bucket. */
export interface UploadResult {
  /** Storage object key within the bucket (`<family_id>/<entity_id>/<filename>`). */
  readonly path: string;
}

/** Result of a successful upload into a bucket that is actually public. */
export interface PublicUploadResult extends UploadResult {
  /** Permanent public URL. Only meaningful for a {@link PublicStorageBucket}. */
  readonly publicUrl: string;
}

export interface UploadOptions {
  /** Overwrite an existing object at the same path. Defaults to `false`. */
  readonly upsert?: boolean;
}

/** Thrown when a file fails client-side validation before upload. */
export class StorageValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StorageValidationError';
  }
}

/**
 * Build a collision-resistant object key for a file.
 *
 * The name segment goes through `sanitizeStorageObjectName` from
 * `@/shared/lib/sanitize`, which is where the rule now lives. It used to be a
 * private `sanitizeFileName` in this file — a second, undocumented sanitiser
 * sitting next to the shared one, which is exactly the divergence the
 * single-sanitiser house rule exists to prevent. The behaviour for ASCII names
 * is unchanged by the move; Cyrillic names are transliterated instead of being
 * erased down to their extension.
 */
function buildObjectPath(
  familyId: string,
  entityId: string,
  file: File
): string {
  const unique =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${familyId}/${entityId}/${unique}-${sanitizeStorageObjectName(file.name)}`;
}

/** Lowercase extension of a filename, dot included, or '' when it has none. */
function extensionOf(name: string): string {
  const trimmed = name.trim();
  const dot = trimmed.lastIndexOf('.');
  return dot === -1 ? '' : trimmed.slice(dot).toLowerCase();
}

/**
 * Validate a file against the target bucket's limits. Throws on failure.
 *
 * THE TYPE RULE, which is not symmetric across the buckets and should not be.
 *
 *   * A bucket with NO extension list (`avatars`, `photos`) demands a reported
 *     MIME type on the list. A blank `file.type` fails, exactly as before —
 *     these buckets take images from a picker that always reports one, and
 *     loosening them here would be an unrelated change.
 *   * A bucket WITH an extension list (`attachments`) tolerates a blank
 *     `file.type`, because a browser genuinely reports `''` for `.csv` and for
 *     Office documents it has no entry for, and the extension is what gates
 *     the bucket in that case. A NON-blank type still has to be on the list,
 *     so a `.pdf`-named file the browser recognises as something else is
 *     refused.
 *
 * Both checks are client-side and neither is a security boundary — a crafted
 * request can call the Storage API directly, and only storage RLS plus the
 * bucket's own server-side limits stop that. What this stops is the ordinary
 * accident: a file the app never advertised as acceptable being dropped onto
 * the calendar and silently accepted into a private bucket. The thrown
 * {@link StorageValidationError} carries a Bulgarian message that
 * FileUploadArea now renders next to the failed file.
 */
export function validateFile(bucket: StorageBucket, file: File): void {
  const config = BUCKET_CONFIG[bucket];

  if (file.size > config.maxBytes) {
    const maxMb = Math.round(config.maxBytes / (1024 * 1024));
    throw new StorageValidationError(
      `Файлът е твърде голям. Максимум ${maxMb} MB.`
    );
  }

  const typeIsUnreported =
    file.type === '' && config.allowedExtensions !== null;
  if (
    config.allowedMimeTypes !== null &&
    !typeIsUnreported &&
    !config.allowedMimeTypes.includes(file.type)
  ) {
    throw new StorageValidationError(config.typeErrorMessage);
  }

  if (
    config.allowedExtensions !== null &&
    !config.allowedExtensions.includes(extensionOf(file.name))
  ) {
    throw new StorageValidationError(config.typeErrorMessage);
  }
}

/**
 * Public URL for an object in a bucket that is actually public.
 *
 * Typed to {@link PublicStorageBucket} so it cannot be called for `photos` or
 * `attachments`, where the URL it builds is a 404 dressed up as a link.
 */
export function getPublicUrl(
  bucket: PublicStorageBucket,
  path: string
): string {
  const client = getSupabaseClient();
  const { data } = client.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

// ============================================
// Storage keys
// ============================================

/**
 * Route prefixes a Supabase Storage URL can carry before `<bucket>/<key>`.
 *
 * Both the plain object routes and the image-render routes, in all three access
 * modes, because several of the six have been written into `photos.url` over
 * this project's life: `getPublicUrl` produced the first, the since-deleted
 * generate-thumbnail function produced the render variant (into the duplicate
 * `thumbnail_url` column that 024 makes optional and 025 drops), and a signed
 * URL is what a hand-fix or a paste would leave behind.
 */
const STORAGE_PATH_MARKERS: readonly string[] = [
  '/storage/v1/object/public/',
  '/storage/v1/object/sign/',
  '/storage/v1/object/authenticated/',
  '/storage/v1/render/image/public/',
  '/storage/v1/render/image/sign/',
  '/storage/v1/render/image/authenticated/',
];

/** Percent-decode, or hand back the input when it is not valid encoding. */
function decodeOrRaw(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Coerce a stored `photos.url` value to a raw object key, whatever shape it
 * arrives in.
 *
 * WHY THIS EXISTS. Migration 020 rewrites `photos.url` — and the duplicate
 * `thumbnail_url` beside it, which 024 makes optional and 025 drops — from
 * rendered public URLs to raw keys. That UPDATE runs once, against rows
 * written by several different producers over the life of the project, and
 * anything it does not recognise it leaves alone. A row it misses would arrive here as a full URL,
 * `createSignedUrls` would refuse it, and that photo would be blank forever
 * with nothing in the UI to explain why. This is the read path's own defence
 * and it costs one string scan per row.
 *
 * A value that is already a bare key is returned untouched — including its
 * percent-encoding, because a key is stored raw and decoding it would name a
 * different object.
 *
 * THE '?' / '#' AMBIGUITY, resolved the OTHER way from orphanScan. A '?' after
 * the bucket segment is either a delimiter (a render URL's `?width=300`, a
 * signed URL's `?token=`) or a literal character of the key — `getPublicUrl`
 * builds URLs with `encodeURI`, which escapes NEITHER character, so both reach
 * the column raw. cleanup-orphan-storage's `extractObjectKey` refuses to guess
 * and returns null, because there a wrong guess ends in a DELETED photo. Here
 * the worst outcome of a wrong guess is a signature for a key that does not
 * exist, which the Storage API rejects and which renders as the onError
 * placeholder. So this function does cut at the delimiter: the safe direction
 * is opposite in the two files, and the two must not be "unified".
 *
 * @returns the object key, or `''` when the value is not a Storage URL for this
 *          bucket (an external URL, another bucket's) and so cannot be signed.
 */
export function storageKeyFromValue(
  bucket: StorageBucket,
  value: string
): string {
  const trimmed = value.trim();
  if (trimmed === '') {
    return '';
  }

  // A bare key: no scheme, no leading slash. Return verbatim.
  if (!trimmed.includes('://') && !trimmed.startsWith('/')) {
    return trimmed;
  }

  const bucketPrefix = `${bucket}/`;
  for (const marker of STORAGE_PATH_MARKERS) {
    const at = trimmed.indexOf(marker);
    if (at === -1) {
      continue;
    }

    const rest = trimmed.slice(at + marker.length);
    if (!rest.startsWith(bucketPrefix)) {
      continue;
    }

    let key = rest.slice(bucketPrefix.length);
    const query = key.indexOf('?');
    if (query !== -1) {
      key = key.slice(0, query);
    }
    const hash = key.indexOf('#');
    if (hash !== -1) {
      key = key.slice(0, hash);
    }
    if (key === '') {
      continue;
    }
    return decodeOrRaw(key);
  }

  return '';
}

// ============================================
// Signed URLs
// ============================================

/**
 * Create a time-limited signed URL for ONE object.
 *
 * Throws on failure. That is deliberate and unchanged: the existing callers
 * (FileUploadArea, supabaseCalendarService.rowToAttachment) treat a failure as
 * fatal, and turning the throw into a silent `''` here would hide a broken
 * attachment. The batch signer below makes the opposite choice for the opposite
 * reason — see there.
 *
 * @param expiresIn Lifetime in seconds. Defaults to {@link SIGNED_URL_TTL_SECONDS}.
 * @param options   `download` forces a `Content-Disposition`; `transform`
 *                  resizes at read time.
 */
export async function createSignedUrl(
  bucket: StorageBucket,
  path: string,
  expiresIn: number = SIGNED_URL_TTL_SECONDS,
  options?: SignedUrlOptions
): Promise<string> {
  const client = getSupabaseClient();
  const { data, error } = await client.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn, options);
  if (error || !data) {
    throw new Error(
      `Неуспешно генериране на връзка: ${error?.message ?? 'unknown error'}`
    );
  }
  return data.signedUrl;
}

/**
 * One entry of a `createSignedUrls` response.
 *
 * Every field is nullable because the Storage API reports per-item failures
 * inside a 200: an object that does not exist, or that the caller's RLS policy
 * refuses, comes back as `{ error: "...", path: null, signedUrl: null }`
 * alongside the entries that worked.
 */
export interface SignedUrlItem {
  readonly path: string | null;
  readonly signedUrl: string | null;
  readonly error?: string | null;
}

/**
 * Fold a `createSignedUrls` response into a `key -> signed URL` lookup.
 *
 * PARTIAL FAILURE IS THE POINT. One deleted object, or one row whose key the
 * policy refuses, must not cost the caller the other 199 signatures — an album
 * with a single missing file has to render as 199 photos and one placeholder,
 * not as an empty grid and not as a thrown fetch. So failed entries are simply
 * absent from the map and the caller's `?? ''` fallback decides what to show.
 *
 * Entries are keyed by the `path` the server echoes back, never by position.
 * The API does return results in request order today, but zipping by index
 * would mean a future reordering hands photo B's signature to photo A — a
 * wrong image shown to a family member, which is both worse and quieter than a
 * blank one. An entry with a null `path` is unkeyable and is dropped; in
 * practice that only happens on entries that also have a null `signedUrl`, so
 * nothing usable is lost.
 *
 * Exported for tests: this is the only part of the signing path vitest can
 * reach, because the Supabase client itself is mocked in the suite.
 */
export function collectSignedUrls(
  items: readonly SignedUrlItem[]
): Map<string, string> {
  const byPath = new Map<string, string>();
  for (const item of items) {
    if (item.path === null || item.signedUrl === null) {
      continue;
    }
    byPath.set(item.path, item.signedUrl);
  }
  return byPath;
}

/**
 * Sign MANY objects in ONE request.
 *
 * This is not a convenience wrapper, it is the difference between an album
 * opening and an album hanging. `supabaseCalendarService.rowToAttachment` signs
 * one object per await, which is fine for the three attachments on an event and
 * is not fine for a 200-photo album: the same shape there is 200 sequential
 * HTTP POSTs every time someone opens the album — 400 back when every row
 * carried its one key a second time in `photos.thumbnail_url`. The Storage API
 * takes the whole list in a single POST to `/object/sign/<bucket>`.
 *
 * NEVER THROWS, and never rejects. A read path that throws takes the whole
 * fetch with it — that is exactly the live Wave-4 finding against
 * `rowToAttachment`, where one unreadable attachment makes the entire calendar
 * load fail. Here a total failure returns an empty map: captions, comments,
 * counts and the album itself still render, and the images fall back to their
 * placeholders.
 *
 * NO `transform` OPTION, and that is not an oversight on this side: the batch
 * endpoint has no transform parameter at all. The transformations are baked
 * into the signature server-side, so a transform cannot be bolted onto a
 * batch-signed URL afterwards either — rewriting `/object/sign/` to
 * `/render/image/sign/` and appending `?width=` yields a token that was not
 * signed for those transformations and the API rejects it. Reach for
 * {@link createSignedUrl} with a `transform` when ONE object needs resizing —
 * `ALBUM_COVER_TRANSFORM` in supabaseAlbumService is this project's only such
 * caller — and accept full-size bytes when a whole list is signed at once.
 *
 * Empty and duplicate paths are dropped before the request, so callers can hand
 * over one key per row without deduping first: a cover key that repeats inside
 * the page, or an empty `url` on a row migration 020 could not repair, costs
 * nothing. Callers used to pass `[row.url, row.thumbnail_url]` per row and this
 * dedupe halved the request, because those two were always the same key — which
 * is the argument that retired the column (024 makes it optional, 025 drops it).
 *
 * @returns `key -> signed URL`, missing any key that could not be signed.
 */
export async function createSignedUrls(
  bucket: StorageBucket,
  paths: readonly string[],
  expiresIn: number = SIGNED_URL_TTL_SECONDS
): Promise<ReadonlyMap<string, string>> {
  const unique = [...new Set(paths.filter((path) => path !== ''))];
  if (unique.length === 0) {
    return new Map();
  }

  try {
    const client = getSupabaseClient();
    const { data, error } = await client.storage
      .from(bucket)
      .createSignedUrls(unique, expiresIn);
    if (error || !data) {
      console.warn(
        `[storage] batch signing failed for ${unique.length} object(s) in "${bucket}": ${error?.message ?? 'unknown error'}`
      );
      return new Map();
    }
    return collectSignedUrls(data);
  } catch (err) {
    // A transport-level failure (offline, DNS, CORS) rejects rather than
    // returning an error envelope. Same answer: no signatures, no throw.
    const reason = err instanceof Error ? err.message : String(err);
    console.warn(`[storage] batch signing threw for "${bucket}": ${reason}`);
    return new Map();
  }
}

// ============================================
// Upload / remove
// ============================================

/**
 * Upload a file into `<family_id>/<entityId>/<filename>`.
 *
 * The `family_id` segment is resolved from the current session so callers only
 * provide the entity (album id, profile id, …).
 *
 * The overloads are the honest part: a public bucket yields a `publicUrl`, a
 * private bucket yields only the `path`, and on the private ones the property
 * does not exist to be misused. Callers on `photos` / `attachments` store
 * `result.path` and sign at read time.
 */
/*
 * `no-redeclare` is core ESLint's rule and it does not know what a TypeScript
 * overload signature is — it sees four declarations of one name and objects.
 * The typed replacement, `@typescript-eslint/no-redeclare`, is not enabled in
 * eslint.config.js, and that config is shared and not this brief's to edit, so
 * the suppression is local and scoped to exactly these four lines. tsc is the
 * check that actually matters here and it is green.
 */
/* eslint-disable no-redeclare */
export function uploadFile(
  bucket: PublicStorageBucket,
  entityId: string,
  file: File,
  options?: UploadOptions
): Promise<PublicUploadResult>;
export function uploadFile(
  bucket: PrivateStorageBucket,
  entityId: string,
  file: File,
  options?: UploadOptions
): Promise<UploadResult>;
export function uploadFile(
  bucket: StorageBucket,
  entityId: string,
  file: File,
  options?: UploadOptions
): Promise<UploadResult>;
export async function uploadFile(
  bucket: StorageBucket,
  entityId: string,
  file: File,
  options: UploadOptions = {}
): Promise<PublicUploadResult | UploadResult> {
  /* eslint-enable no-redeclare */
  validateFile(bucket, file);

  const familyId = await getCurrentFamilyId();
  const path = buildObjectPath(familyId, entityId, file);
  const client = getSupabaseClient();

  const { error } = await client.storage.from(bucket).upload(path, file, {
    cacheControl: '3600',
    ...(file.type ? { contentType: file.type } : {}),
    upsert: options.upsert ?? false,
  });

  if (error) {
    throw new Error(`Качването е неуспешно: ${error.message}`);
  }

  if (isPublicBucket(bucket)) {
    return { path, publicUrl: getPublicUrl(bucket, path) };
  }
  return { path };
}

// ============================================
// Removal
// ============================================

/**
 * Object keys per `remove()` call.
 *
 * `storage.remove()` takes an array and there is no documented ceiling, but the
 * whole list travels as one JSON body and the API answers with one entry per
 * key, so a single call for a 2,000-photo album is a request and a response
 * that can both time out — and a timeout mid-delete tells the caller nothing
 * about what was removed. Chunking bounds both, and a failed chunk costs that
 * chunk instead of the run.
 *
 * 100 matches `DELETE_CHUNK_SIZE` in cleanup-orphan-storage/orphanScan.ts,
 * which is the other place in this project that mass-deletes storage objects.
 * The two are independent constants in independent runtimes (Deno vs the
 * browser bundle) and cannot import each other; keeping the NUMBER equal means
 * one behaviour to reason about.
 */
export const REMOVE_CHUNK_SIZE = 100;

/**
 * Split `paths` into batches of at most `size`, dropping empties and duplicates.
 *
 * Pure and exported for tests — it is the only part of the removal path vitest
 * can reach, because everything below it goes through the mocked Supabase
 * client. An empty entry matters: `remove([''])` is not a no-op request, it is
 * a request naming the bucket root.
 */
export function chunkPaths(
  paths: readonly string[],
  size: number = REMOVE_CHUNK_SIZE
): readonly (readonly string[])[] {
  const step = Math.max(1, Math.floor(size));
  const unique = [...new Set(paths.filter((path) => path.trim() !== ''))];
  const batches: string[][] = [];
  for (let at = 0; at < unique.length; at += step) {
    batches.push(unique.slice(at, at + step));
  }
  return batches;
}

/**
 * Coerce a stored value to an object key that is SAFE TO DELETE, or `''`.
 *
 * {@link storageKeyFromValue} with one extra rule, and the rule is the whole
 * point: a value carrying '?' or '#' is REFUSED rather than cut at the
 * delimiter.
 *
 * That is the opposite of what the read path does, deliberately, and it is the
 * same split cleanup-orphan-storage makes between its own two helpers. On a
 * READ a wrong guess costs a signature for a key that does not exist, which
 * renders as a placeholder. On a DELETE a wrong guess REMOVES SOMEONE ELSE'S
 * OBJECT: `.../photos/<family>/<album>/holiday?2.jpg` cut at the '?' names
 * `<family>/<album>/holiday`, and if that key happens to exist it is now gone,
 * with no undo and no error anywhere. `getPublicUrl` builds URLs with
 * `encodeURI`, which escapes NEITHER character, so both really do reach a
 * stored column raw.
 *
 * In practice this app cannot produce such a key — `sanitizeStorageObjectName`
 * (in `@/shared/lib/sanitize`, formerly the private `sanitizeFileName` in this
 * file) keeps only word characters, dot and dash, and transliterates Cyrillic
 * into that same ASCII set rather than widening it — so the rule costs nothing
 * on data this project wrote. That ASCII-only guarantee is load-bearing HERE,
 * which is why the display-name sanitiser beside it, which preserves Unicode,
 * is a separate function and must not be substituted for it.
 * It is here for the values it did NOT write: a hand-edited row,
 * a restored dump, and above all a stored SIGNED URL, which always carries
 * `?token=<jwt>` and must never be parsed into a deletion target.
 *
 * @returns the object key, or `''` when the value is not an unambiguous key for
 *          this bucket. `''` means DELETE NOTHING.
 */
export function storageKeyForRemoval(
  bucket: StorageBucket,
  value: string
): string {
  const trimmed = value.trim();
  if (trimmed.includes('?') || trimmed.includes('#')) {
    return '';
  }
  return storageKeyFromValue(bucket, trimmed);
}

/**
 * Report a storage removal that did not happen, without failing the caller.
 *
 * Every removal in this module is BEST-EFFORT, and this is where that policy is
 * spent rather than merely declared. The row delete that precedes a removal is
 * the user's actual intent and it has already committed; turning a Storage
 * error into a thrown exception there would report "deletion failed" for a
 * photo that is genuinely gone from the album, and — on the paths that catch
 * and roll back — would put the row back. A leftover object is not a
 * correctness problem, it is a bill: cleanup-orphan-storage reclaims any
 * `photos` object no row references, after its 24-hour grace window.
 */
function reportRemovalFailure(
  bucket: StorageBucket,
  paths: readonly string[],
  reason: string
): void {
  monitoringService.captureError(
    new Error(`Storage removal failed in "${bucket}": ${reason}`),
    {
      tags: { feature: 'storage', action: 'remove' },
      level: 'warning',
      extra: { bucket, count: paths.length, paths: paths.slice(0, 20) },
    }
  );
}

/**
 * Remove MANY objects from a bucket. Best-effort: never throws, never rejects.
 *
 * WHY NEVER THROWING IS THE CONTRACT AND NOT A CONVENIENCE. Every caller
 * reaches this AFTER the database row that referenced the object is already
 * deleted. There is nothing left to roll back to, the user has been told the
 * photo is gone, and it is gone. A throw here would either surface a false
 * failure or — worse, on the paths that catch and restore optimistic state —
 * undo a delete the user asked for because a bucket call timed out. So a
 * failure is captured to monitoring and the caller is not told.
 *
 * PARTIAL FAILURE IS TOLERATED PER CHUNK. One bad batch does not stop the
 * others: deleting a 900-photo album must not leave 800 objects behind because
 * chunk 2 of 9 timed out. Each chunk is reported on its own.
 *
 * Empty and duplicate paths are dropped by {@link chunkPaths}, so an all-empty
 * list issues no request at all. That is not tidiness — `remove([''])` names
 * the bucket root.
 */
export async function removeFiles(
  bucket: StorageBucket,
  paths: readonly string[]
): Promise<void> {
  const batches = chunkPaths(paths);
  if (batches.length === 0) {
    return;
  }

  const client = getSupabaseClient();
  for (const batch of batches) {
    try {
      const { error } = await client.storage.from(bucket).remove([...batch]);
      if (error) {
        reportRemovalFailure(bucket, batch, error.message);
      }
    } catch (err) {
      // A transport-level failure (offline, DNS, CORS) rejects rather than
      // returning an error envelope. Same answer: report it, keep going.
      reportRemovalFailure(
        bucket,
        batch,
        err instanceof Error ? err.message : String(err)
      );
    }
  }
}

/**
 * Remove a single object from a bucket. Best-effort — see {@link removeFiles}.
 *
 * A thin alias, so this project has ONE removal policy rather than two. It used
 * to throw on failure and it had zero callers, so nothing depended on that
 * contract; wiring it up while keeping the throw would have meant every new call
 * site re-deciding whether a failed object delete should fail a completed row
 * delete. It should not, everywhere, so the decision lives here instead of in
 * each caller's `catch`.
 */
export async function removeFile(
  bucket: StorageBucket,
  path: string
): Promise<void> {
  await removeFiles(bucket, [path]);
}

/**
 * Remove the object a STORED COLUMN VALUE points at. Best-effort.
 *
 * For the columns that hold a rendered URL rather than a raw key — today that
 * is `profiles.avatar_url`, because `avatars` is the one bucket that stays
 * public. Derivation goes through {@link storageKeyForRemoval}, so a value this
 * function cannot resolve UNAMBIGUOUSLY removes nothing rather than guessing.
 *
 * A value that resolves to `''` is deliberately NOT reported to monitoring: the
 * commonest reason is an avatar hosted somewhere else entirely (an OAuth
 * provider's picture URL), which is not a failure and would be pure noise.
 */
export async function removeFileByValue(
  bucket: StorageBucket,
  value: string
): Promise<void> {
  const key = storageKeyForRemoval(bucket, value);
  if (key === '') {
    return;
  }
  await removeFiles(bucket, [key]);
}
